import { VideoCamera } from '@liveauctioneers/hammer-ui-core/icons';
import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { useCallback } from 'react';
import scrollIntoView from 'scroll-into-view';

import { useAppSelector } from '@/redux/hooks';
import {
    getCatalogHasVideo,
    getCatalogIsTimedPlus,
    getCatalogLongAddress,
    getCatalogTitle,
    getShouldShowLive,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getLiveCatalogStatus } from '@/redux/modules/console';
import { getLocale } from '@/redux/modules/intl';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { popupCenter } from '@/utils/popupCenter';
import { getAuctionVideoUrl } from '@/utils/urls';

import { CatalogDate } from './components/CatalogDate/CatalogDate';

type Props = {
    catalogId: number;
    forceIsLive: boolean;
};

const WIDTH = 1024;
const HEIGHT = 567;

const scrollToFooter = () => {
    const catalogFooter = document.getElementById('catalog-footer-header');
    scrollIntoView(catalogFooter);
};

export const CatalogDetails = ({ catalogId, forceIsLive }: Props) => {
    const location = useAppSelector((state) => getCatalogLongAddress(state, catalogId));
    const hasVideo = useAppSelector((state) => getCatalogHasVideo(state, catalogId));
    const isTimedPlus = useAppSelector((state) => getCatalogIsTimedPlus(state, catalogId));
    const title = useAppSelector((state) => getCatalogTitle(state, catalogId));
    const locale = useAppSelector(getLocale);
    const formatMessage = useAppFormatMessage();

    // Live catalog data selectors
    const showLive = useAppSelector((state) => getShouldShowLive(state, catalogId));
    const liveStatus = useAppSelector((state) => getLiveCatalogStatus(state, catalogId));
    const isLive = showLive || liveStatus === 'live';
    const localePath = locale === 'en-gb' ? `/${locale}` : '';

    const openVideoConsole = useCallback(() => {
        if (!isLive) {
            return;
        }
        popupCenter({
            height: HEIGHT,
            title: `${title} Video`,
            url: localePath + getAuctionVideoUrl(catalogId),
            width: WIDTH,
        });
    }, [catalogId, isLive, localePath, title]);

    return (
        <section
            className="mt-[30px] flex flex-row items-center justify-start xsMax:mb-lg xsMax:mt-0 xsMax:flex-col xsMax:text-center [&>span]:h-5 [&>span]:leading-5"
            data-testid="catalog-component-date"
        >
            {hasVideo && !isTimedPlus && (
                <VideoCamera
                    className="mr-xs cursor-pointer xsMax:mb-xs xsMax:mr-0"
                    color="primary"
                    data-testid="video-indicator"
                    onClick={openVideoConsole}
                    size="sm"
                    style="fill"
                />
            )}
            <CatalogDate
                catalogId={catalogId}
                forceIsLive={forceIsLive}
            />
            <div className="ml-sm flex h-full border-r border-border-secondary pr-sm xsMax:mb-xs xsMax:ml-0 xsMax:border-r-0 xsMax:pr-0">
                <BodySecondary color="secondary">{location}</BodySecondary>
            </div>
            <div
                className="ml-sm flex h-full pr-sm"
                onClick={scrollToFooter}
            >
                <BodySecondary
                    className="cursor-pointer underline hover:text-[#0097ba]"
                    color="secondary"
                    data-testid="catalogAuctionDetailsLink"
                >
                    {formatMessage(['auction_details'])}
                </BodySecondary>
            </div>
        </section>
    );
};
